import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import ArticleDataService from '../services/ArticleService';
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { Button, Input, ActionButton, TableHeader } from './common';
import { tableStyles } from '../styles/tailwind.classes';
import log from "../services/logger";

const ArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();

  const retrieveArticles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ArticleDataService.getAll();
      
      const groupedData = response.data.reduce((groups, article) => {
        const brandName = article.brand.brandName;
        if (!groups[brandName]) groups[brandName] = [];
        groups[brandName].push(article);
        return groups;
      }, {});

      const sortedData = Object.values(groupedData)
        .map(group => group.sort((a, b) => a.name.localeCompare(b.name)))
        .flat();

      setArticles(sortedData);
    } catch (error) {
      toast.error('Greška pri učitavanju artikala');
      log.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    retrieveArticles();
  }, [retrieveArticles]);

  const filteredArticles = useMemo(() => {
    return articles.filter(article =>
      article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.code.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [articles, searchTerm]);

  const columns = useMemo(() => [
    {
      Header: 'Šifra',
      accessor: 'code',
      width: '100px',
    },
    {
      Header: 'GTIN',
      accessor: 'barcode',
      width: '120px',
    },
    {
      Header: 'Naziv',
      accessor: 'name',
      width: '250px',
    },
    {
      Header: 'Brend',
      accessor: 'brand.brandName',
      width: '120px',
    },
    {
      Header: 'Jed. mere',
      accessor: 'unitOfMeasurement',
      width: '100px',
    },
    {
      Header: 'Cena',
      accessor: 'wholesalePrice',
      width: '120px',
      Cell: ({ value }) => (
        <span className="font-medium">{Number(value).toFixed(2)} RSD</span>
      ),
    },
    {
      Header: 'Status',
      accessor: 'isActive',
      width: '100px',
      Cell: ({ value }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Aktivan' : 'Neaktivan'}
        </span>
      ),
    },
    {
      Header: 'Akcije',
      id: 'actions',
      width: '150px',
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <ActionButton
            onClick={() => navigate(`/articles/edit/${row.original.article_id}`)}
            icon="fa-edit"
            label="Izmeni artikal"
            variant="primary"
          />
          <ActionButton
            onClick={() => handleVisibilityToggle(row.original)}
            icon={row.original.isVisible ? 'fa-eye-slash' : 'fa-eye'}
            label={row.original.isVisible ? 'Sakrij artikal' : 'Prikaži artikal'}
            variant={row.original.isVisible ? 'danger' : 'success'}
          />
          <ActionButton
            onClick={() => handleStatusToggle(row.original)}
            icon={row.original.isActive ? 'fa-ban' : 'fa-check'}
            label={row.original.isActive ? 'Deaktiviraj' : 'Aktiviraj'}
            variant={row.original.isActive ? 'warning' : 'success'}
          />
        </div>
      ),
    },
  ], [navigate]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: filteredArticles,
      initialState: { pageSize }
    },
    useSortBy,
    usePagination
  );

  const handleVisibilityToggle = async (article) => {
    try {
      if (article.isVisible) {
        await ArticleDataService.hideArticle(article.article_id);
      } else {
        await ArticleDataService.showArticle(article.article_id);
      }
      await retrieveArticles();
      toast.success(`Artikal uspešno ${article.isVisible ? 'sakriven' : 'prikazan'}`);
    } catch (error) {
      toast.error('Greška pri promeni vidljivosti artikla');
    }
  };

  const handleStatusToggle = async (article) => {
    try {
      if (article.isActive) {
        await ArticleDataService.deactivateArticle(article.article_id);
      } else {
        await ArticleDataService.activateArticle(article.article_id);
      }
      await retrieveArticles();
      toast.success(`Artikal uspešno ${article.isActive ? 'deaktiviran' : 'aktiviran'}`);
    } catch (error) {
      toast.error('Greška pri promeni statusa artikla');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
        <div className="relative flex-1 max-w-md">
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pretraži artikle po nazivu ili šifri..."
            className="pl-10"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className="fas fa-search text-gray-400"></i>
          </div>
        </div>
        
        <Button
          onClick={() => navigate('/articles/add')}
          variant="primary"
          className="inline-flex items-center"
        >
          <i className="fas fa-plus mr-2"></i>
          Dodaj Artikal
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table {...getTableProps()} className={tableStyles.table}>
                <thead className={tableStyles.header}>
                  {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map(column => (
                        <TableHeader
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          sortable={column.canSort}
                          sorted={column.isSorted}
                          sortDirection={column.isSortedDesc ? 'desc' : 'asc'}
                        >
                          {column.render('Header')}
                        </TableHeader>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
                  {page.map(row => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        className={`hover:bg-gray-50 ${
                          !row.original.isActive ? 'bg-red-50' : ''
                        }`}
                      >
                        {row.cells.map(cell => (
                          <td
                            {...cell.getCellProps()}
                            className={tableStyles.cell}
                            style={{ width: cell.column.width }}
                          >
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  variant="secondary"
                >
                  Prethodna
                </Button>
                <Button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  variant="secondary"
                >
                  Sledeća
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Prikazano{' '}
                    <span className="font-medium">{pageIndex * pageSize + 1}</span>
                    {' '}-{' '}
                    <span className="font-medium">
                      {Math.min((pageIndex + 1) * pageSize, filteredArticles.length)}
                    </span>
                    {' '}od{' '}
                    <span className="font-medium">{filteredArticles.length}</span>
                    {' '}rezultata
                  </p>
                </div>
                <div className="flex space-x-2">
                  <select
                    value={pageSize}
                    onChange={e => {
                      setPageSize(Number(e.target.value));
                      setTablePageSize(Number(e.target.value));
                    }}
                    className="form-select rounded-md border-gray-300"
                  >
                    {[10, 20, 30, 40, 50].map(size => (
                      <option key={size} value={size}>
                        Prikaži {size}
                      </option>
                    ))}
                  </select>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <Button
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                      variant="secondary"
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </Button>
                    <Button
                      onClick={() => nextPage()}
                      disabled={!canNextPage}
                      variant="secondary"
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ArticlesList;
